/** Build login/signup URL with post-auth redirect (e.g. hire flow). */
export const AUTH_ACTIONS = {
  HIRE: "hire",
  BOOK: "book",
  CALL: "call",
  CHAT: "chat",
};

export function resolveAuthAction(searchParams) {
  if (!searchParams) return null;
  const action = searchParams.get("action");
  if (action) return action;
  if (searchParams.get("hire")) return AUTH_ACTIONS.HIRE;
  return null;
}

export function resolveAuthCaregiverId(searchParams) {
  if (!searchParams) return null;
  return searchParams.get("caregiverId") || searchParams.get("hire") || null;
}

/** Where to send a customer after login/sign-up when a protected action was pending. */
export function buildPostCustomerAuthNavigation(searchParams, { loginMessage, bookingId } = {}) {
  const returnTo = searchParams?.get("returnTo");
  const caregiverId = resolveAuthCaregiverId(searchParams);
  const action = resolveAuthAction(searchParams);
  const state = { loginMessage };

  if (bookingId) state.bookingId = bookingId;

  if (
    caregiverId &&
    (action === AUTH_ACTIONS.HIRE || action === AUTH_ACTIONS.BOOK)
  ) {
    const searchLocation = searchParams?.get("searchLocation") || "";
    const lat = searchParams?.get("lat") || "";
    const lng = searchParams?.get("lng") || "";
    const date = searchParams?.get("date") || "";
    const marketplaceSearchPrefill =
      searchLocation && lat && lng
        ? {
            locationLabel: searchLocation,
            coords: { latitude: lat, longitude: lng },
            ...(date ? { searchDate: date } : {}),
          }
        : null;
    return {
      path: "/marketplace",
      replace: true,
      state: {
        ...state,
        bookingResume: true,
        openHireCaregiver: {
          id: caregiverId,
          caregiverId,
          isQualified: true,
        },
        hireCaregiverId: caregiverId,
        ...(marketplaceSearchPrefill ? { marketplaceSearchPrefill } : {}),
      },
    };
  }

  if (action === AUTH_ACTIONS.CHAT && caregiverId) {
    return {
      path: `/messenger?caregiverId=${encodeURIComponent(caregiverId)}`,
      replace: true,
      state,
    };
  }

  if (returnTo) {
    const returnPath = String(returnTo).split("?")[0].trim();
    // Booking shell needs a caregiver — bare /marketplace/booking is a blank page.
    if (returnPath === "/marketplace/booking" || returnPath.startsWith("/marketplace/booking/")) {
      if (caregiverId) {
        return {
          path: "/marketplace/booking",
          replace: true,
          state: {
            ...state,
            hireCaregiverId: caregiverId,
            bookingResume: true,
            resumeAction: action || AUTH_ACTIONS.HIRE,
          },
        };
      }
      return { path: "/dashboard", replace: true, state };
    }
    // Plain marketplace return without a hire/book action → customer home (dashboard).
    if (
      (returnPath === "/marketplace" || returnPath === "/") &&
      !caregiverId &&
      action !== AUTH_ACTIONS.HIRE &&
      action !== AUTH_ACTIONS.BOOK &&
      action !== AUTH_ACTIONS.CHAT
    ) {
      return { path: "/dashboard", replace: true, state };
    }
    if (caregiverId) {
      state.caregiverId = caregiverId;
      if (action) state.resumeAction = action;
      if (action === AUTH_ACTIONS.HIRE || action === AUTH_ACTIONS.BOOK) {
        state.hireCaregiverId = caregiverId;
        state.bookingResume = true;
      }
    }
    return { path: returnTo, replace: true, state };
  }

  if (bookingId) {
    return { path: "/dashboard", replace: true, state };
  }

  return { path: "/dashboard", replace: true, state };
}

export function buildLoginUrl({ returnTo = "/dashboard", hire, tab = "login", role } = {}) {
  if (hire != null && hire !== "" && !role) {
    return buildAuthOverlayUrl(resolvePublicOverlayPath(returnTo), {
      returnTo,
      hire,
      tab,
      role,
    });
  }
  const params = new URLSearchParams();
  if (returnTo) params.set("returnTo", returnTo);
  if (hire != null && hire !== "") params.set("hire", String(hire));
  if (tab) params.set("tab", tab);
  if (role) params.set("role", role);
  return `/login?${params.toString()}`;
}

/** Public page that should stay visible behind the auth overlay. */
export function resolvePublicOverlayPath(returnTo, fallback = "/") {
  const path = String(returnTo || "")
    .split("?")[0]
    .trim();
  if (path.startsWith("/caregivers/")) return path;
  if (path === "/" || path === "") return "/";
  if (path === "/become-caregiver") return "/become-caregiver";
  if (path === "/pricing" || path.startsWith("/pricing/")) return "/pricing";
  if (path.startsWith("/marketplace") || path.startsWith("/dashboard")) return fallback;
  return fallback;
}

export function shouldRedirectLoginToOverlay(searchParams) {
  if (!searchParams) return false;
  if (searchParams.get("admin") === "1") return false;
  const returnTo = searchParams.get("returnTo");
  const hire = searchParams.get("hire");
  if (hire) return true;
  if (returnTo && returnTo !== "/login" && !returnTo.startsWith("/admin")) return true;
  return false;
}

/** Open sign-in as an overlay on the current browse page (keeps background visible). */
export function buildAuthOverlayUrl(
  basePath = "/",
  {
    returnTo = "/marketplace",
    hire,
    action,
    caregiverId,
    tab = "login",
    role,
    searchLocation,
    lat,
    lng,
    date,
    planType,
  } = {}
) {
  const params = new URLSearchParams();
  params.set("auth", "login");
  if (returnTo) params.set("returnTo", returnTo);
  const resolvedCaregiverId = caregiverId ?? hire;
  if (resolvedCaregiverId != null && resolvedCaregiverId !== "") {
    params.set("caregiverId", String(resolvedCaregiverId));
  }
  if (hire != null && hire !== "") params.set("hire", String(hire));
  if (action) params.set("action", action);
  if (tab) params.set("tab", tab);
  if (role) params.set("role", role);
  if (searchLocation) params.set("searchLocation", searchLocation);
  if (lat != null && lat !== "") params.set("lat", String(lat));
  if (lng != null && lng !== "") params.set("lng", String(lng));
  if (date) params.set("date", date);
  if (planType) params.set("planType", planType);
  const query = params.toString();
  const base = basePath || "/";
  return query ? `${base}?${query}` : base;
}

export function clearAuthOverlayParams(searchParams) {
  const next = new URLSearchParams(searchParams);
  [
    "auth",
    "hire",
    "action",
    "caregiverId",
    "tab",
    "role",
    "returnTo",
    "searchLocation",
    "lat",
    "lng",
    "date",
    "planType",
  ].forEach((key) => next.delete(key));
  return next;
}
